import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchCompanyClient,
  GetCompanyClient,
  DeleteCompanyClient,
  UpdateCompanyClient,
  CreateCompanyClient,
  ArchivingCompanyClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

const searchCompanyClient = new SearchCompanyClient(SERVICE_URL)
const getCompanyClient = new GetCompanyClient(SERVICE_URL)
const createCompanyClient = new CreateCompanyClient(SERVICE_URL)
const deleteCompanyClient = new DeleteCompanyClient(SERVICE_URL)
const updateCompanyClient = new UpdateCompanyClient(SERVICE_URL)
const archivingCompanyClient = new ArchivingCompanyClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  detail: false,
  excel: null,
  company: null,
}

// Async Thunks
export const companyGetAll = createAsyncThunk(
  'company/getAll',
  async ({params, companyTypeId}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchCompanyClient.search(
        params.name,
        companyTypeId,
        params.hasBalance,
        params.categoryId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setCompanyList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const companyGetId = createAsyncThunk('company/getById', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await getCompanyClient.get(id)
    dispatch(setCompanyDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const companyDelete = createAsyncThunk('company/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await deleteCompanyClient.delete(params)
    dispatch(setDeleted(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const companyCreate = createAsyncThunk('company/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await createCompanyClient.create(params)
    dispatch(setCompanyCreated(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const companyUpdate = createAsyncThunk('company/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await updateCompanyClient.update(params)
    dispatch(setCompanyUpdated(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const companyArchiving = createAsyncThunk(
  'company/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await archivingCompanyClient.archive(params)
      dispatch(setArchived(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const companyExcel = createAsyncThunk(
  'company/excel',
  async ({params, companyTypeId, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchCompanyClient.excel(
        params.name,
        companyTypeId,
        params.hasBalance,
        params.categoryId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setCompanyList: (state, action) => {
      state.company = action.payload
      state.loading = false
    },
    setCompanyDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state) => {
      state.isDeleted = true
      state.loading = false
    },
    setCompanyCreated: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setCompanyUpdated: (state) => {
      state.isRedirect = true
      state.loading = false
    },
    resetRedirect: (state) => {
      state.isRedirect = false
    },
    setArchived: (state) => {
      state.isArchived = true
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('company/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

export const {
  setLoading,
  setError,
  setCompanyList,
  setCompanyDetail,
  setDeleted,
  setCompanyCreated,
  setCompanyUpdated,
  setArchived,
  setExcel,
  resetState,
  resetRedirect,
} = companySlice.actions

// Selectors
export const selectCompanyLoading = (state) => state.company.loading
export const selectCompanyError = (state) => state.company.error
export const selectCompanyList = (state) => state.company.company
export const selectCompanyDetail = (state) => state.company.detail
export const selectCompanyIsDeleted = (state) => state.company.isDeleted
export const selectCompanyIsArchived = (state) => state.company.isArchived
export const selectCompanyExcel = (state) => state.company.excel

export default companySlice.reducer
